import React, { useMemo, useState } from 'react';
import type { Vehicle } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useVehicleDrawerStore } from '@hero/hooks-shared';
import { useVehicleApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import { PencilIcon, UserIcon } from '@hero/web-icons';
import { StackedDrawer } from '../../../app/Drawer/StackedDrawer';
import type { ActionButtonGroup } from '../../../app/ActionButton/ActionButton';
import { VehicleSummaryTab } from './VehicleSummaryTab';
import { VehicleOrdersTab } from './VehicleOrdersTab';
import { DrawerHeader } from '../../../app/Drawer/DrawerHeader';
import { DrawerTabs } from '../../../app/Drawer/DrawerTabs';
import { DrawerContent } from '../../../app/Drawer/DrawerContent';
import { DrawerFooter } from '../../../app/Drawer/DrawerFooter';
import { VehicleAssignDriverModal } from './VehicleAssignDriverModal';
import { VehicleEditModal } from '../Modal/VehicleEditModal';
import { formatPlateForDisplay } from '../../../app/Select/SelectAsync/selectorEntityRowUtils';

export interface VehicleDrawerProps {
  syncUrl?: boolean | string;
}

interface VehicleDrawerContentProps {
  vehicle: Vehicle;
  onClose: () => void;
}

const VehicleDrawerContent: React.FC<VehicleDrawerContentProps> = ({ vehicle, onClose }) => {
  const { t } = useTranslation(['m-vehicle']);
  const { isEnabled: canEditVehicle } = useFeature('vehicle_module', 'sub_edit_vehicle');
  const [activeTab, setActiveTab] = useState('summary');
  const [assignDriverOpen, setAssignDriverOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const tabs = [
    { id: 'summary', label: t('drawer.tabs.summary') },
    { id: 'orders', label: t('drawer.tabs.orders') },
  ];

  const title = `${t(`types.${vehicle.classification.type}`, {
    defaultValue: vehicle.classification.type,
  })} · ${formatPlateForDisplay(vehicle.registration.plate)}`;

  const actions: ActionButtonGroup[] = useMemo(
    () => [
      {
        id: 'assign-driver',
        label: t('drawer.actions.assign_driver'),
        description: t('drawer.action_assign_driver_description'),
        icon: <UserIcon className="w-4 h-4" />,
        onClick: () => setAssignDriverOpen(true),
      },
      {
        id: 'edit',
        label: t('drawer.actions.edit'),
        description: t('drawer.action_edit_vehicle_description'),
        icon: <PencilIcon className="w-4 h-4" />,
        onClick: () => setEditModalOpen(true),
        hide: !canEditVehicle,
      },
    ],
    [t, canEditVehicle],
  );

  const renderTab = () => {
    switch (activeTab) {
      case 'summary':
        return <VehicleSummaryTab vehicle={vehicle} />;
      case 'orders':
        return <VehicleOrdersTab vehicle={vehicle} />;
      default:
        return null;
    }
  };

  return (
    <>
      <DrawerHeader
        title={title}
        subtitle={vehicle.driver?.name ?? t('drawer.no_driver_assigned')}
        onClose={onClose}
      />
      <DrawerTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      <DrawerContent>{renderTab()}</DrawerContent>
      <DrawerFooter actions={actions} buttonLabel={t('table.columns.actions')} />

      <VehicleAssignDriverModal
        isOpen={assignDriverOpen}
        onClose={() => setAssignDriverOpen(false)}
        vehicle={vehicle}
      />
      <VehicleEditModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        vehicleId={vehicle._id}
      />
    </>
  );
};

export const VehicleDrawer: React.FC<VehicleDrawerProps> = ({ syncUrl }) => (
  <StackedDrawer<Vehicle>
    useStore={useVehicleDrawerStore}
    useItemApi={id => useVehicleApi(id)}
    syncUrl={syncUrl}
    defaultParamKey="vehicleId"
    sheetClassName="flex flex-col p-0 bg-white [&>button[class*='absolute']]:hidden"
    title="Vehicle Details"
    description="View vehicle information"
    renderContent={(vehicle, onClose) => (
      <VehicleDrawerContent vehicle={vehicle} onClose={onClose} />
    )}
  />
);
